import React from "react";
import DatasourceCardLayout from "../../../shared/cards/DatasourceCardLayout";
import AccordionLayout, { accordionExpandedCacheKey } from "../../../shared/accordions/AccordionLayout";
import TakeOffSourceLogo from "./TakeOffSourceLogo";
import TakeOffActionsButtons from "./TakeOffActionsButtons";
import { useQtoSummary } from "./qtoSummary";
import { useDatasources } from "../../../context/DatasourceContext";
import { TAKE_OFF_DELETE_NOTICE } from "../../../constants";
import { QTO_SOURCES, type QtoSourceId } from "../../../../integrations/qto/registry";
import type { AggregatedQto } from "../../../../integrations/qto/types";
import { workbookCache } from "../../../../excel/workbook/cache";

/**
 * Everything a take-off card needs that varies by source. Each source supplies one of these from a
 * `use<Source>CardModel` hook; the card itself is identical across sources.
 */
export type TakeOffCardModel = {
  /** False when the source holds no selection, so the card renders nothing. */
  present: boolean;
  /** Primary line: the package, project or file the card represents. */
  title: string;
  /** Optional line beneath the title, before the QTO count. */
  secondary?: string;
  aggregatedQtos: AggregatedQto[] | null;
  takeoffItemCount: number | null;
  lastUpdated: string | null;
  emptyTooltip: string;
  /** Omitted by sources with nowhere to open — the layout hides the button. */
  onLaunch?: () => void;
  /** Omitted by sources that cannot refresh in place, e.g. an uploaded file. */
  onRefresh?: () => void;
  /** Source-specific teardown. Removing the card and clearing its cache key is handled here. */
  onDisconnect: () => void | Promise<void>;
};

interface TakeOffDatasourceCardProps {
  sourceId: QtoSourceId;
  model: TakeOffCardModel;
  onCardClick: () => void;
}

const TakeOffDatasourceCard: React.FC<TakeOffDatasourceCardProps> = ({ sourceId, model, onCardClick }) => {
  const { removeDatasource } = useDatasources();
  const cacheKey = accordionExpandedCacheKey(sourceId);

  const summary = useQtoSummary({
    qtoSourceId: sourceId,
    aggregatedQtos: model.aggregatedQtos,
    takeoffItemCount: model.takeoffItemCount,
    lastUpdated: model.lastUpdated,
    emptyTooltip: model.emptyTooltip,
  });

  if (!model.present) return null;

  const handleRemoveCard = async () => {
    removeDatasource("takeOff", sourceId);
    void workbookCache.delete(cacheKey);
    await model.onDisconnect();
  };

  return (
    <DatasourceCardLayout
      header={{ text: <TakeOffSourceLogo type={sourceId} />, name: QTO_SOURCES[sourceId].label }}
      card={{
        text: model.title,
        subtext: [model.secondary, `${summary.totalText} QTOs`].filter(Boolean).join(" • "),
      }}
      onCardClick={onCardClick}
      onLaunch={model.onLaunch}
      onRefresh={model.onRefresh}
      onClose={handleRemoveCard}
      deleteNotice={TAKE_OFF_DELETE_NOTICE}
    >
      <AccordionLayout
        details={[
          { label: "Total QTOs", value: summary.totalText },
          { label: "New QTOs", value: summary.newText },
          { label: "Updated QTOs", value: summary.updatedText },
        ]}
        lastUpdated={model.lastUpdated ?? undefined}
        cacheKey={cacheKey}
      >
        <TakeOffActionsButtons summary={summary} />
      </AccordionLayout>
    </DatasourceCardLayout>
  );
};

export default TakeOffDatasourceCard;
